import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Popconfirm,
  Tooltip,
  Tag,
  Typography,
  Alert,
  Switch,
  Space,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const STATUS_COLOURS = {
  active: "green",
  expired: "orange",
  revoked: "red",
  deleted: "default",
};

const DEFAULT_EXPIRY_DAYS = 90;
const MAX_EXPIRY_DAYS = 365;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const ApiCredentials = () => {
  const { permissions } = usePermissions();
  const canManage = permissions?.includes(PERMISSIONS.MANAGE_API_CREDENTIALS);

  const [form] = Form.useForm();
  const [credentials, setCredentials] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 20,
    totalSize: 0,
  });

  // Held only in memory, only until the modal is dismissed. The API returns the plaintext once
  // and cannot return it again, so there is deliberately nowhere else to read it from.
  const [revealedToken, setRevealedToken] = useState(null);

  const fetchScopes = useCallback(async () => {
    try {
      const res = await axios.get("/api/api-credentials/scopes");
      setAvailableScopes(res.data.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/api-credentials", {
        params: {
          page: pagination.currentPage,
          limit: pagination.limit,
          includeDeleted: includeDeleted ? "true" : undefined,
        },
      });
      setCredentials(res.data.data || []);
      setPagination((prev) => ({
        ...prev,
        totalSize: res.data.pagination?.totalSize || 0,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, includeDeleted]);

  useEffect(() => {
    fetchScopes();
  }, [fetchScopes]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const openCreate = () => {
    form.setFieldsValue({
      name: "",
      scopes: availableScopes.length === 1 ? [availableScopes[0].scope] : [],
      expiresInDays: DEFAULT_EXPIRY_DAYS,
    });
    setCreateOpen(true);
  };

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const res = await axios.post("/api/api-credentials", {
        name: values.name,
        scopes: values.scopes,
        expiresInDays: values.expiresInDays,
      });

      setCreateOpen(false);
      form.resetFields();
      setRevealedToken(res.data.data?.token || null);
      fetchCredentials();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (credential) => {
    try {
      await axios.post(`/api/api-credentials/${credential.objectId}/revoke`);
      message.success(`"${credential.name}" revoked. It can no longer authenticate.`);
      fetchCredentials();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const handleDelete = async (credential) => {
    try {
      await axios.delete(`/api/api-credentials/${credential.objectId}`);
      message.success(`"${credential.name}" deleted.`);
      fetchCredentials();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Token",
      dataIndex: "tokenPrefix",
      key: "tokenPrefix",
      render: (prefix) => <code>{prefix ? `${prefix}…` : "-"}</code>,
    },
    {
      title: "Scopes",
      dataIndex: "scopes",
      key: "scopes",
      render: (scopes) =>
        (scopes || []).map((scope) => (
          <Tag key={scope} color="blue">
            {scope}
          </Tag>
        )),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={STATUS_COLOURS[status] || "default"}>{status}</Tag>,
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (createdBy) => createdBy || "-",
    },
    {
      title: "Last used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (value) => formatDateTime(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, credential) => {
        if (!canManage || credential.status === "deleted") {
          return null;
        }

        const isRevoked = credential.status === "revoked";

        return (
          <Space>
            {!isRevoked && (
              <Popconfirm
                title={`Revoke "${credential.name}"? Any integration using it stops working immediately.`}
                okText="Revoke"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={() => handleRevoke(credential)}
              >
                <Button size="small" danger>
                  Revoke
                </Button>
              </Popconfirm>
            )}
            {isRevoked ? (
              <Popconfirm
                title={`Delete "${credential.name}"? It is hidden from this list but kept for audit.`}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={() => handleDelete(credential)}
              >
                <Button size="small" danger>
                  Delete
                </Button>
              </Popconfirm>
            ) : (
              <Tooltip title="Revoke the credential before deleting it.">
                <Button size="small" disabled>
                  Delete
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="API Credentials"
        childRoutes={[{ path: "/api-credentials", breadcrumbName: "API Credentials" }]}
      />

      <Alert
        type="info"
        showIcon
        className="mb-2"
        message="Machine credentials for integrations"
        description="These tokens authenticate automated integrations only — they cannot be used to sign in. A token is shown once, at creation, and is stored hashed. Every create, revoke and delete is recorded in the Admin audit log."
      />

      <div className="flex mb-2" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Space>
          <Switch
            checked={includeDeleted}
            onChange={(checked) => {
              setIncludeDeleted(checked);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
          />
          <span>Show deleted</span>
        </Space>

        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create credential
          </Button>
        )}
      </div>

      <Table
        rowKey="objectId"
        loading={loading}
        columns={columns}
        dataSource={credentials}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.limit,
          total: pagination.totalSize,
          showSizeChanger: false,
          onChange: (page) => setPagination((prev) => ({ ...prev, currentPage: page })),
        }}
      />

      <Modal
        title="Create API credential"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Give the credential a name" }]}
            extra="Name it after what will use it, e.g. n8n-support-tickets."
          >
            <Input placeholder="n8n-support-tickets" />
          </Form.Item>

          <Form.Item
            name="scopes"
            label="Scopes"
            rules={[{ required: true, message: "Select at least one scope" }]}
          >
            <Select mode="multiple" placeholder="Select scopes" optionLabelProp="value">
              {availableScopes.map((item) => (
                <Select.Option key={item.scope} value={item.scope}>
                  {item.scope} — {item.description}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="expiresInDays"
            label="Expires in (days)"
            rules={[{ required: true, message: "Set an expiry" }]}
            extra={`Maximum ${MAX_EXPIRY_DAYS} days. The credential stops working on this date.`}
          >
            <InputNumber min={1} max={MAX_EXPIRY_DAYS} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Copy this token now"
        visible={!!revealedToken}
        onCancel={() => setRevealedToken(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setRevealedToken(null)}>
            I have copied it
          </Button>,
        ]}
        // Tears the subtree down on dismiss rather than leaving a modal that once held a
        // plaintext credential mounted for the rest of the session.
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          className="mb-2"
          message="This token will not be shown again"
          description="It is stored hashed, so nobody — including engineers — can retrieve it later. If it is lost, revoke this credential and create another."
        />
        <Typography.Paragraph copyable={{ text: revealedToken }} code style={{ wordBreak: "break-all" }}>
          {revealedToken}
        </Typography.Paragraph>
      </Modal>
    </>
  );
};

export default ApiCredentials;
